import React, { useState, useEffect } from 'react';
import { useContext } from 'react';
import { FirmProfileContext } from '../context/FirmProfileContext';
import { baseURL } from '../apiConfig';
import './ItemMasterSettings.css';

const ItemMasterSettings = () => {
    const { firmProfile } = useContext(FirmProfileContext);
    const [masterData, setMasterData] = useState({
        metal: 'Gold',
        purity: '22',
        itemGroup: 'Ring',
        itemName: '',
        currentStock: 0,
        currentWeight: 0.000,
        reorderLevel: 5,
        remarks: ''
    });

    const [masterList, setMasterList] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchMasterData = async () => {
        setLoading(true);
        try {
            // URL ke ant me timestamp lagaya hai taaki browser humesha fresh data laye
            const response = await fetch(`${baseURL}/api/master/all/?t=${new Date().getTime()}`, {
                cache: 'no-store'
            });
            const result = await response.json();
            if (result.success) {
                setMasterList(result.data);
            } else {
                alert("Error fetching data: " + result.message);
            }
        } catch (error) {
            console.error("Error connecting to server:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMasterData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMasterData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!masterData.metal || !masterData.purity || !masterData.itemGroup) {
            alert("Metal, Purity aur Item Group zaroori fields hain!");
            return;
        }

        try {
            const response = await fetch(`${baseURL}/api/master/save/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(masterData)
            });
            const result = await response.json();

            if (result.success) {
                alert(result.message);
                setMasterData(prev => ({
                    ...prev,
                    itemName: '',
                    currentStock: 0,
                    currentWeight: 0.000,
                    reorderLevel: 5,
                    remarks: ''
                }));
                fetchMasterData();
            } else {
                alert("Action Failed: " + result.message);
            }
        } catch (error) {
            alert("Server push crash!");
        }
    };

    // 🖨️ ReOrder List Print Logic
    const handlePrintReorderList = () => {
        const reorderItems = masterList.filter(item => item.currentStock <= item.reorderLevel);

        if (reorderItems.length === 0) {
            alert("Koi bhi item reorder level par nahi hai.");
            return;
        }

        const goldItems = reorderItems.filter(item => item.metal.toLowerCase() === 'gold');
        const silverItems = reorderItems.filter(item => item.metal.toLowerCase() === 'silver');

        const printWindow = window.open('', '_blank');
        
        const renderTableRows = (items) => {
            if (items.length === 0) return '<tr><td colspan="6" style="text-align:center;">Koi item reorder list me nahi hai</td></tr>';
            return items.map(item => `
                <tr>
                    <td>${item.purity}K</td>
                    <td>${item.itemGroup}</td>
                    <td>${item.itemName || '—'}</td>
                    <td>${item.currentStock} Pcs</td>
                    <td>${item.reorderLevel} Pcs</td>
                    <td>${item.currentWeight.toFixed(3)} g</td>
                </tr>
            `).join('');
        };

        const printContent = `
            <html>
                <head>
                    <title>ReOrder Stock List - ${firmProfile.firm_name}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
                        h2 { text-align: center; color: #b34a00; margin-bottom: 5px; }
                        h4 { text-align: center; margin-top: 0; color: #555; }
                        .category-title { background: #1a1e24; color: #ffaa00; padding: 8px 12px; margin-top: 20px; font-size: 16px; border-radius: 4px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
                        th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; }
                        @media print {
                            body { padding: 0; }
                            .no-print { display: none; }
                        }
                    </style>
                </head>
                <body>
                    <h2>${firmProfile.firm_name}</h2>
                    <h4>Low Stock ReOrder Report</h4>
                    <hr />

                    <div class="category-title">🟡 GOLD ITEMS (LOW STOCK)</div>
                    <table>
                        <thead>
                            <tr>
                                <th>Purity</th>
                                <th>Group</th>
                                <th>Item Name</th>
                                <th>Current Stock</th>
                                <th>Alert Limit</th>
                                <th>Current Weight</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${renderTableRows(goldItems)}
                        </tbody>
                    </table>

                    <div class="category-title">⚪ SILVER ITEMS (LOW STOCK)</div>
                    <table>
                        <thead>
                            <tr>
                                <th>Purity</th>
                                <th>Group</th>
                                <th>Item Name</th>
                                <th>Current Stock</th>
                                <th>Alert Limit</th>
                                <th>Current Weight</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${renderTableRows(silverItems)}
                        </tbody>
                    </table>
                </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 300);
    };

    return (
        <div className="master-settings-container">
            <div className="master-settings-header">Shop Master Item Settings & Current Stock</div>
            
            <div className="master-layout-grid">
                <form className="master-form-panel" onSubmit={handleSubmit}>
                    <fieldset className="master-fieldset">
                        <legend>Configure Items</legend>
                        
                        <div className="master-field">
                            <label>Metal / Type</label>
                            <select name="metal" value={masterData.metal} onChange={handleInputChange}>
                                <option value="Gold">Gold</option>
                                <option value="Silver">Silver</option>
                            </select>
                        </div>

                        <div className="master-field">
                            <label>Purity Standard</label>
                            <input type="text" name="purity" value={masterData.purity} placeholder="e.g. 22, 18, 750" onChange={handleInputChange} />
                        </div>

                        <div className="master-field">
                            <label>Item Group</label>
                            <input type="text" name="itemGroup" value={masterData.itemGroup} placeholder="e.g. Ring, Tika, Payal" onChange={handleInputChange} />
                        </div>

                        <div className="master-field">
                            <label>Item Name</label>
                            <input type="text" name="itemName" value={masterData.itemName} placeholder="e.g. Dulhan Tika" onChange={handleInputChange} />
                        </div>

                        <div className="master-field-row">
                            <div className="master-field">
                                <label>Initial Stock (Pcs) 🔒</label>
                                <input 
                                    type="number" 
                                    name="currentStock" 
                                    value={0} 
                                    readOnly 
                                    style={{ cursor: 'not-allowed', opacity: 0.6 }}
                                    title="Auto controlled. Manual entry disabled."
                                />
                            </div>
                            <div className="master-field">
                                <label>Initial Weight (g) 🔒</label>
                                <input 
                                    type="number" 
                                    name="currentWeight" 
                                    value={0} 
                                    readOnly 
                                    style={{ cursor: 'not-allowed', opacity: 0.6 }}
                                    title="Auto controlled. Manual entry disabled."
                                />
                            </div>
                        </div>

                        <div className="master-field">
                            <label style={{ color: '#d32f2f', fontWeight: 'bold' }}>⚠️ Reorder Alert Level (Qty)</label>
                            <input type="number" name="reorderLevel" value={masterData.reorderLevel} onChange={handleInputChange} />
                        </div>

                        <div className="master-field">
                            <label>Remarks / Notes</label>
                            <textarea name="remarks" value={masterData.remarks} placeholder="Write something regarding parameters..." onChange={handleInputChange} />
                        </div>

                        <button type="submit" className="btn-master-save">🔒 Save Master Configuration</button>
                    </fieldset>
                </form>

                <div className="master-grid-panel">
                    <div className="grid-header-row">
                        <h3>Active Items Table with Current Stock</h3>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="button" className="btn-reorder" onClick={handlePrintReorderList}>
                                📄 ReOrder List
                            </button>
                            <button type="button" className="btn-refresh" onClick={fetchMasterData}>
                                🔄 Refresh
                            </button>
                        </div>
                    </div>

                    <div className="master-table-scroll">
                        <table className="master-real-table">
                            <thead>
                                <tr>
                                    <th>Metal</th>
                                    <th>Purity</th>
                                    <th>Group</th>
                                    <th>Item Name</th>
                                    <th>Stock</th>
                                    <th>Weight</th>
                                    <th>Alert Limit</th>
                                    <th>Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="8" style={{ textAlign: 'center' }}>Loading Master parameters...</td></tr>
                                ) : masterList.length === 0 ? (
                                    <tr><td colSpan="8" style={{ textAlign: 'center', color: '#888' }}>Koi master entry database me nahi mili.</td></tr>
                                ) : (
                                    masterList.map((item) => (
                                        <tr key={item.id} className={item.currentStock <= item.reorderLevel ? "row-alert" : ""}>
                                            <td><strong>{item.metal}</strong></td>
                                            <td>{item.purity}K</td>
                                            <td style={{ color: '#0288d1', fontWeight: 'bold' }}>{item.itemGroup}</td>
                                            <td>{item.itemName || '—'}</td>
                                            <td>
                                                <span className={item.currentStock <= item.reorderLevel ? "stock-badge alert" : "stock-badge"}>
                                                    {item.currentStock} Pcs
                                                </span>
                                            </td>
                                            <td>{item.currentWeight.toFixed(3)} g</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#c62828' }}>{item.reorderLevel}</td>
                                            <td className="remarks-td">{item.remarks || '—'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ItemMasterSettings;